import { Menu, MenuItems, MenuItem, Transition } from '@headlessui/react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, useEffect, useRef } from 'react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

export type MenuOption = {
  label: string;
  id: string;
  icon: JSX.Element;
  disabled?: boolean;
};

type Props = {
  isDropdownVisible: boolean;
  setDropdownVisible: (visible: boolean) => void;
  mousePosition: { x: number; y: number };
  menuOptions?: MenuOption[];
  onMenuOptionClick?: (id: string) => void;
};

const GridMenuDropdown = (props: Props) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { getColour } = useThemeContext();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        props.setDropdownVisible(false);
      }
    };

    if (props.isDropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [props.isDropdownVisible, props.setDropdownVisible]);

  return (
    <AnimatePresence>
      {props.isDropdownVisible && (
        <div
          ref={dropdownRef}
          className="absolute shadow-sm rounded-2xl py-2"
          style={{
            top: props.mousePosition.y,
            left: props.mousePosition.x - 200,
            zIndex: 1000,
            backgroundColor: getColour('thirdLayer')
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <Menu as="div">
              <Transition as={Fragment} show={props.isDropdownVisible}>
                <MenuItems
                  style={{
                    zIndex: 1000,
                    backgroundColor: getColour('thirdLayer'),
                    borderColor: getColour('border')
                  }}
                  className="absolute -left-12 z-95 mt-0 p-1 w-64 rounded-2xl shadow-lg border focus:outline-none"
                >
                  {props.menuOptions &&
                    props.menuOptions.map((option, index) => (
                      <MenuItem key={index}>
                        <button
                          onClick={() => {
                            if (!option.disabled) {
                              props.onMenuOptionClick?.(option.id);
                              props.setDropdownVisible(false);
                            }
                          }}
                          className={cn(
                            'flex items-center px-4 py-2 text-sm w-full text-left rounded-2xl transition-colors',
                            option.disabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'hover:bg-primary/5'
                          )}
                          style={{
                            color: option.disabled
                              ? getColour('inactiveText')
                              : getColour('normalText')
                          }}
                          disabled={option.disabled}
                        >
                          <span
                            className={cn(
                              'mr-2',
                              option.disabled && 'cursor-not-allowed'
                            )}
                          >
                            {option.icon}
                          </span>
                          <span
                            className={
                              option.disabled ? 'cursor-not-allowed' : ''
                            }
                          >
                            {option.label}
                          </span>
                        </button>
                      </MenuItem>
                    ))}
                </MenuItems>
              </Transition>
            </Menu>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
export default GridMenuDropdown;
